import React, { useState, useRef, useEffect } from 'react';
import '../Style/GoalCard/gC-entry.css';
import CharCounter from '../components/CharCounter';

function UserEntryList({ entries, onDeleteEntry, onRenameEntry, isReadOnly, notesPrivate }) {
  const [editingId, setEditingId] = useState(null);
  const [tempText, setTempText] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const textareaRef = useRef(null);
  const editAreaRef = useRef(null);
  const NOTES_MAX = 2000;

  useEffect(() => {
    if (editingId && textareaRef.current) {
      textareaRef.current.style.height = '42px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = scrollHeight + 'px';
      textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [tempText, editingId]);

  const formatNotifTime = (createdAt) => {
    if (!createdAt) return '';
    const date = new Date(createdAt.endsWith('Z') ? createdAt : createdAt + 'Z');
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleSave = (id) => {
    const original = entries.find((e) => e.id === id);
    if (tempText.trim() !== '' && tempText !== original?.description) {
      onRenameEntry(id, tempText);
    }
    setEditingId(null);
  };

  // ✅ New discard handler
  const handleDiscard = () => {
    setEditingId(null);
    setTempText('');
  };

  if (isReadOnly && notesPrivate) {
    return (
      <div className="private-notes-placeholder">
        <span className="lock-icon">🔒</span>
        <p>Notes are private</p>
      </div>
    );
  }

  if (!entries?.length) return <div className="empty-entries">No notes yet.</div>;

  return (
    <div className="entry-feed">
      {entries.map((entry) => (
        <div
          key={entry.id}
          ref={editingId === entry.id ? editAreaRef : null}
          className={`entry-item ${editingId === entry.id ? 'is-editing' : ''}`}
        >
          <div className="entry-content">
            <span className="entry-dot"></span>

            {!isReadOnly && editingId === entry.id ? (
              <div className="edit-container">
                <div className="textarea-wrapper">
                  <textarea
                    maxLength={NOTES_MAX}
                    ref={textareaRef}
                    className="inline-edit-textarea"
                    value={tempText}
                    onChange={(e) => setTempText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave(entry.id);
                      if (e.key === 'Escape') handleDiscard();
                    }}
                    autoFocus
                  />
                  <CharCounter current={tempText.length} max={NOTES_MAX} />
                </div>

                <div className="edit-helper-row">
                  <span className="helper-text">Esc to discard • Ctrl+Enter to save</span>
                  {/* ✅ Both buttons explicitly labelled */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="discard-note-btn" onClick={handleDiscard}>
                      Discard
                    </button>
                    <button className="save-note-btn" onClick={() => handleSave(entry.id)}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="entry-text-group"
                onClick={() => {
                  if (!isReadOnly) {
                    setEditingId(entry.id);
                    setTempText(entry.description || entry.text);
                  }
                }}
                style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
              >
                <p className="entry-description" style={{ whiteSpace: 'pre-wrap' }}>
                  {entry.description || entry.text}
                </p>

                <span className="entry-date">
                  Created • {entry.createdAt ? formatNotifTime(entry.createdAt) : ''}
                  {entry.updatedAt && entry.updatedAt !== entry.createdAt
                    ? ` · Edited • ${formatNotifTime(entry.updatedAt)}`
                    : ''}
                </span>
              </div>
            )}
          </div>

          {!isReadOnly && (
            <div className="entry-actions-shield">
              {confirmDeleteId === entry.id ? (
                <div className="delete-confirmation">
                  <button className="confirm-btn" onClick={() => onDeleteEntry(entry.id)}>
                    Delete
                  </button>
                  <button className="cancel-btn" onClick={() => setConfirmDeleteId(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="entry-delete-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(entry.id);
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default UserEntryList;
